/**
 * Rebrandly Conversion Tracking SDK
 * Main entry point
 */

import { setDebugMode, log, warn, error as logError } from './logger.js';
import { initializeClickId, getCurrentClickId, getUtmParamsFromStorage, getVerifyTokenFromUrl, DEFAULT_URL_PARAM } from './storage.js';
// TODO: Re-enable signature functionality later
// import { createSignature, isSubtleCryptoAvailable } from './signature.js';
import { sendEvent, sendPing, isValidBaseUrl } from './api.js';
import {
  isDuplicateEvent,
  clearDedupCache,
  cleanupExpiredEntries,
  getDedupStats,
  DEFAULT_CONFIG as DEFAULT_DEDUP_CONFIG
} from './deduplication.js';
import {
  startQueueProcessor,
  clearQueue,
  getQueueStats,
  processQueue
} from './queue.js';

// API paths (appended to apiBase)
const API_PATHS = {
  track: '/v1/track',
  pageView: '/v1/track/page-view',
  conversion: '/v1/track/conversion',
  ping: '/v1/sdk/ping'
};

// SDK state
let config = {
  apiKey: null,
  apiBase: process.env.API_BASE,
  clickIdParam: DEFAULT_URL_PARAM, // URL query parameter name for click ID
  customUtmParams: [], // Custom UTM parameter names to track
  autoPageView: true, // Automatically track pageview on init
  verifyParam: 'rbly_verify', // Query parameter name for domain verification
  initialized: false,
  deduplication: {
    ...DEFAULT_DEDUP_CONFIG
  }
};

// SPA navigation listener state
let spaListeners = null;
let lastTrackedPath = null;

/**
 * Initialize the Rebrandly SDK
 * Must be called before using track()
 *
 * @param {Object} options - Configuration options
 * @param {string} options.apiKey - Your Rebrandly API key (required)
 * @param {string} [options.apiBase] - Custom API base URL, e.g. 'https://track.rebrandly.com' (optional)
 * @param {Array<string>} [options.customUtmParams] - Additional custom UTM parameter names to track (optional)
 * @param {boolean} [options.autoPageView=true] - Automatically track pageview on init when click ID exists (optional)
 * @param {Object} [options.deduplication] - Deduplication configuration (optional)
 * @param {boolean} [options.deduplication.enabled=true] - Enable/disable deduplication
 * @param {Array<string>} [options.deduplication.idempotencyKeys] - Property names to use for deduplication
 * @param {number} [options.deduplication.timeWindow=10000] - Time window (ms) for time-based deduplication
 * @param {number} [options.deduplication.retention] - Retention period (ms) for dedup cache
 * @throws {Error} If required parameters are missing or invalid
 */
function init(options) {
  // Validate options
  if (!options || typeof options !== 'object') {
    throw new Error('[Rebrandly SDK] Init options must be an object');
  }

  if (!options.apiKey || typeof options.apiKey !== 'string') {
    throw new Error('[Rebrandly SDK] apiKey is required and must be a string');
  }

  if (options.debug !== undefined) {
    setDebugMode(options.debug === true);
  }

  // TODO: Re-enable signature functionality later
  // Check browser compatibility
  // if (!isSubtleCryptoAvailable()) {
  //   logError(
  //     '[Rebrandly SDK] SubtleCrypto API not available. ' +
  //     'Conversion tracking requires a modern browser with Web Crypto API support.'
  //   );
  //   throw new Error('Browser does not support required cryptography features');
  // }

  // Reset config to defaults before applying new options
  config = {
    apiKey: options.apiKey,
    apiBase: process.env.API_BASE,
    clickIdParam: DEFAULT_URL_PARAM,
    customUtmParams: [],
    autoPageView: true,
    verifyParam: 'rbly_verify',
    initialized: false,
    deduplication: {
      ...DEFAULT_DEDUP_CONFIG
    }
  };

  // Validate custom base URL if provided
  if (options.apiBase) {
    if (!isValidBaseUrl(options.apiBase)) {
      throw new Error('[Rebrandly SDK] apiBase must be a valid HTTP/HTTPS URL');
    }
    // Strip trailing slash for consistent path joining
    config.apiBase = options.apiBase.replace(/\/+$/, '');
  }

  // Validate and store custom click ID parameter name if provided
  if (options.clickIdParam !== undefined) {
    if (typeof options.clickIdParam !== 'string' || !options.clickIdParam) {
      throw new Error('[Rebrandly SDK] clickIdParam must be a non-empty string');
    }
    config.clickIdParam = options.clickIdParam;
  }

  // Validate and store custom UTM parameters if provided
  if (options.customUtmParams) {
    if (!Array.isArray(options.customUtmParams)) {
      throw new Error('[Rebrandly SDK] customUtmParams must be an array of strings');
    }
    config.customUtmParams = options.customUtmParams;
  }

  // Validate and store autoPageView setting if provided
  if (options.autoPageView !== undefined) {
    if (typeof options.autoPageView !== 'boolean') {
      throw new Error('[Rebrandly SDK] autoPageView must be a boolean');
    }
    config.autoPageView = options.autoPageView;
  }

  // Validate and store verifyParam setting if provided
  if (options.verifyParam !== undefined) {
    if (typeof options.verifyParam !== 'string' || options.verifyParam.length === 0) {
      throw new Error('[Rebrandly SDK] verifyParam must be a non-empty string');
    }
    config.verifyParam = options.verifyParam;
  }

  // Validate and store deduplication configuration if provided
  if (options.deduplication !== undefined) {
    if (typeof options.deduplication !== 'object' || options.deduplication === null) {
      throw new Error('[Rebrandly SDK] deduplication must be an object');
    }

    // Merge with defaults
    config.deduplication = {
      ...DEFAULT_DEDUP_CONFIG,
      ...options.deduplication
    };

    // Validate specific dedup options
    if (config.deduplication.idempotencyKeys && !Array.isArray(config.deduplication.idempotencyKeys)) {
      throw new Error('[Rebrandly SDK] deduplication.idempotencyKeys must be an array');
    }

    if (config.deduplication.timeWindow && typeof config.deduplication.timeWindow !== 'number') {
      throw new Error('[Rebrandly SDK] deduplication.timeWindow must be a number');
    }

    if (config.deduplication.retention && typeof config.deduplication.retention !== 'number') {
      throw new Error('[Rebrandly SDK] deduplication.retention must be a number');
    }
  }

  // Mark as initialized
  config.initialized = true;

  // Teardown SPA listeners from previous init (if re-initializing)
  teardownSpaNavigation();
  lastTrackedPath = null;

  // Clean up expired deduplication entries
  if (config.deduplication.enabled) {
    cleanupExpiredEntries(config.deduplication.retention);
  }

  // Initialize click ID and UTM parameter management
  const clickId = initializeClickId(undefined, config.customUtmParams, config.clickIdParam);

  if (clickId) {
    log('[Rebrandly SDK] Initialized with click ID:', clickId);
  } else {
    log('[Rebrandly SDK] Initialized (no click ID found)');
  }

  if (config.deduplication.enabled) {
    log('[Rebrandly SDK] Deduplication enabled');
  }

  // Start the queue processor for background retry of failed events
  // Wrap sendEvent with skipQueue=true to avoid infinite loops
  const sendEventForQueue = (endpoint, payload, apiKey) => {
    return sendEvent(endpoint, payload, apiKey, true); // skipQueue=true
  };
  startQueueProcessor(sendEventForQueue);

  // Check for domain verification token in URL
  const verifyToken = getVerifyTokenFromUrl(config.verifyParam);
  if (verifyToken) {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';

    // Fire-and-forget: send verification ping without blocking init
    setTimeout(() => {
      sendPing(config.apiBase + API_PATHS.ping, verifyToken, domain, config.apiKey).then(result => {
        if (result.success) {
          log('[Rebrandly SDK] Domain verification ping sent successfully');
        } else {
          warn('[Rebrandly SDK] Domain verification ping failed:', result.message);
        }
      }).catch(error => {
        warn('[Rebrandly SDK] Domain verification ping error:', error.message);
      });
    }, 0);
  }

  // Automatically track pageview if enabled and click ID exists
  if (config.autoPageView && clickId) {
    // Record current path so SPA listener skips it (avoids double-fire on first load)
    lastTrackedPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : null;

    setTimeout(() => {
      trackSdkPageView({
        url: typeof window !== 'undefined' ? window.location.href : '',
        path: typeof window !== 'undefined' ? window.location.pathname : ''
      }).catch(error => {
        warn('[Rebrandly SDK] Failed to track automatic page_view:', error.message);
      });
    }, 0);
  }

  // Setup SPA navigation listeners if autoPageView is enabled
  if (config.autoPageView) {
    setupSpaNavigation();
  }
}

/**
 * Track a conversion event
 *
 * @param {string} event - The event name (e.g., 'signup', 'purchase', 'download')
 * @param {Object} [properties={}] - Optional event properties for custom metadata
 * @param {number|null} [value=null] - Optional monetary value for the conversion
 * @param {string|null} [currency=null] - Optional currency code (e.g., 'USD', 'EUR')
 * @returns {Promise<Object>} Result of the tracking request
 *
 * @example
 * // Simple event
 * rbly.track('signup');
 *
 * @example
 * // Event with monetary value
 * rbly.track('purchase', {product_id: 'prod_123'}, 99.99, 'USD');
 *
 * @example
 * // Event with custom metadata
 * rbly.track('lead', { email: 'user@example.com', source: 'newsletter' });
 */
async function track(event, properties = {}, value = null, currency = null) {
  // Check if SDK is initialized
  if (!config.initialized) {
    logError('[Rebrandly SDK] SDK not initialized. Call rbly.init() first.');
    return {
      success: false,
      error: 'not_initialized',
      message: 'SDK not initialized'
    };
  }

  // Validate event name
  if (!event || typeof event !== 'string') {
    logError('[Rebrandly SDK] Event name is required and must be a string');
    return {
      success: false,
      error: 'invalid_event',
      message: 'Event name is required and must be a string'
    };
  }

  // Reject reserved rb: prefix — these are system events with dedicated methods
  if (event.startsWith('rb:')) {
    logError(
      `[Rebrandly SDK] Event name "${event}" uses the reserved "rb:" prefix. ` +
      'Use rbly.convert() for conversions. Page views are tracked automatically by the SDK.'
    );
    return {
      success: false,
      error: 'reserved_event_name',
      message: `"${event}" uses the reserved "rb:" prefix - use rbly.convert() for conversions`
    };
  }

  // Warn on soft-deprecated event names (still sent for backward compat)
  if (event === 'page_view' || event === 'conversion') {
    warn(
      `[Rebrandly SDK] rbly.track("${event}", ...) is deprecated. ` +
      `${event === 'page_view' ? 'Page views are tracked automatically by the SDK.' : 'Use rbly.convert() instead.'} ` +
      'Sending to /v1/track for backward compatibility.'
    );
  }

  // Get current click_id
  const clickId = getCurrentClickId();

  // If no click_id, skip tracking
  if (!clickId) {
    warn(
      '[Rebrandly SDK] No click ID found. ' +
      `Conversion tracking requires the user to arrive via a Rebrandly link with ?${config.clickIdParam} parameter.`
    );
    return {
      success: false,
      error: 'no_click_id',
      message: 'No click ID found in storage'
    };
  }

  // Generate timestamp
  const timestamp = new Date().toISOString();

  // Validate properties
  if (properties && typeof properties !== 'object') {
    logError('[Rebrandly SDK] Properties must be an object');
    return {
      success: false,
      error: 'invalid_properties',
      message: 'Properties must be an object'
    };
  }

  // Auto-merge stored UTM parameters into properties
  const storedUtmData = getUtmParamsFromStorage();
  let finalProperties = { ...properties };

  if (storedUtmData && storedUtmData.clickId === clickId && storedUtmData.utmParams) {
    // Merge UTM params into properties (UTM params won't override user-provided properties)
    finalProperties = {
      ...storedUtmData.utmParams,
      ...properties
    };
    log('[Rebrandly SDK] Including UTM parameters in conversion:', Object.keys(storedUtmData.utmParams));
  }

  // Check for duplicate event
  const dedupResult = isDuplicateEvent(event, finalProperties, config.deduplication);

  if (dedupResult.isDuplicate) {
    const reasonText = dedupResult.reason === 'property_based'
      ? `duplicate event detected (${dedupResult.key})`
      : 'duplicate event detected (time-based)';

    warn(
      `[Rebrandly SDK] ${reasonText}. ` +
      `Last tracked ${Math.round(dedupResult.timeSinceLastTracked / 1000)}s ago. Event not sent.`
    );

    return {
      success: false,
      error: 'duplicate_event',
      message: 'Duplicate event prevented by deduplication',
      deduplicated: true,
      reason: dedupResult.reason,
      key: dedupResult.key,
      lastTracked: dedupResult.lastTracked,
      timeSinceLastTracked: dedupResult.timeSinceLastTracked
    };
  }

  try {
    // TODO: Re-enable signature functionality later
    // Create HMAC signature
    // const signature = await createSignature(
    //   config.apiKey,
    //   clickId,
    //   event,
    //   timestamp,
    //   value,
    //   currency,
    //   properties
    // );

    // Construct payload (using camelCase to match API expectations)
    const payload = {
      clickId: clickId,
      event: event,
      value: value,
      currency: currency,
      properties: finalProperties,
      timestamp: timestamp
      // TODO: Re-enable signature functionality later
      // signature: signature
    };

    // Send to API
    const result = await sendEvent(config.apiBase + API_PATHS.track, payload, config.apiKey);

    return result;

  } catch (error) {
    logError('[Rebrandly SDK] Error tracking event:', error.message);
    return {
      success: false,
      error: 'tracking_error',
      message: error.message
    };
  }
}


/**
 * Track a conversion event
 *
 * Sends to POST /v1/track/conversion. The server stores the event name as rb:conversion.
 * The label argument is injected into properties.label (method argument wins over properties.label).
 *
 * @param {string} label - Conversion label (required, non-empty string)
 * @param {number|null} [value=null] - Monetary value of the conversion (optional)
 * @param {string|null} [currency=null] - 3-letter ISO currency code, required when value is provided
 * @param {Object} [properties={}] - Optional conversion properties (e.g. orderId, plan)
 * @returns {Promise<Object>} Result of the tracking request
 *
 * @example
 * // Monetary conversion
 * rbly.convert('plan purchase', 19.90, 'USD', { orderId: 'abc' });
 *
 * @example
 * // Non-monetary conversion (e.g. signup)
 * rbly.convert('signup', null, null, { kind: 'signup' });
 */
async function convert(label, value = null, currency = null, properties = {}) {
  if (!config.initialized) {
    logError('[Rebrandly SDK] SDK not initialized. Call rbly.init() first.');
    return { success: false, error: 'not_initialized', message: 'SDK not initialized' };
  }

  if (typeof label !== 'string' || label.trim() === '') {
    logError('[Rebrandly SDK] label is required and must be a non-empty string');
    return { success: false, error: 'invalid_label', message: 'label is required and must be a non-empty string' };
  }

  if (typeof properties !== 'object' || properties === null) {
    logError('[Rebrandly SDK] Properties must be an object');
    return { success: false, error: 'invalid_properties', message: 'Properties must be an object' };
  }

  if (value != null && value !== 0 && !currency) {
    logError('[Rebrandly SDK] currency is required when value is provided');
    return { success: false, error: 'invalid_currency', message: 'currency is required when value is provided' };
  }

  if (value != null && currency && (typeof currency !== 'string' || currency.length !== 3)) {
    logError('[Rebrandly SDK] currency must be a 3-letter ISO code (e.g. "USD")');
    return { success: false, error: 'invalid_currency', message: 'currency must be a 3-letter ISO code' };
  }

  const clickId = getCurrentClickId();
  if (!clickId) {
    warn(
      `[Rebrandly SDK] No click ID found. ` +
      `Conversion tracking requires the user to arrive via a Rebrandly link with ?${config.clickIdParam} parameter.`
    );
    return { success: false, error: 'no_click_id', message: 'No click ID found in storage' };
  }

  const storedUtmData = getUtmParamsFromStorage();
  let finalProperties = { ...properties };
  if (storedUtmData && storedUtmData.clickId === clickId && storedUtmData.utmParams) {
    finalProperties = { ...storedUtmData.utmParams, ...properties };
  }
  // label arg wins over any label that may already be in properties
  finalProperties.label = label;

  // Deduplication — fold value/currency into the fingerprint inputs so two
  // monetary conversions with different amounts don't collide on the shared
  // time:last slot. These keys are dedup-only; the API payload below is unaffected.
  const dedupProperties = value != null
    ? { ...finalProperties, __rbValue: value, __rbCurrency: currency }
    : finalProperties;
  const dedupResult = isDuplicateEvent('rb:conversion', dedupProperties, config.deduplication);
  if (dedupResult.isDuplicate) {
    const reasonText = dedupResult.reason === 'property_based'
      ? `duplicate conversion detected (${dedupResult.key})`
      : 'duplicate conversion detected (time-based)';
    warn(
      `[Rebrandly SDK] ${reasonText}. ` +
      `Last tracked ${Math.round(dedupResult.timeSinceLastTracked / 1000)}s ago. Conversion not sent.`
    );
    return {
      success: false,
      error: 'duplicate_event',
      message: 'Duplicate conversion prevented by deduplication',
      deduplicated: true,
      reason: dedupResult.reason,
      key: dedupResult.key,
      lastTracked: dedupResult.lastTracked,
      timeSinceLastTracked: dedupResult.timeSinceLastTracked
    };
  }

  const payload = {
    clickId,
    properties: finalProperties,
    timestamp: new Date().toISOString()
  };

  // Only include value/currency for monetary conversions
  if (value != null && value !== 0) {
    payload.value = value;
    payload.currency = currency;
  }

  return sendEvent(config.apiBase + API_PATHS.conversion, payload, config.apiKey);
}

/**
 * Send an SDK-owned page_view to the dedicated page-view endpoint.
 * Used for both the initial page load and SPA route changes.
 * Not part of the public API — customers use track() for their own events.
 *
 * @param {Object} properties - Must include `url`; `path` strongly recommended
 * @returns {Promise<Object>}
 */
async function trackSdkPageView(properties = {}) {
  const clickId = getCurrentClickId();
  if (!clickId) return;

  // Merge stored UTM parameters into properties
  const storedUtmData = getUtmParamsFromStorage();
  let finalProperties = { ...properties };
  if (storedUtmData && storedUtmData.clickId === clickId && storedUtmData.utmParams) {
    finalProperties = { ...storedUtmData.utmParams, ...properties };
  }

  const payload = {
    clickId,
    properties: finalProperties,
    timestamp: new Date().toISOString()
  };

  return sendEvent(config.apiBase + API_PATHS.pageView, payload, config.apiKey);
}

/**
 * Monkey-patch history.pushState / replaceState and listen to popstate so the
 * SDK fires a page_view on every SPA route change without requiring manual calls.
 * Only active while autoPageView is true.
 */
function setupSpaNavigation() {
  if (typeof window === 'undefined' || typeof history === 'undefined') return;

  const firePageView = () => {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath === lastTrackedPath) return;
    lastTrackedPath = currentPath;

    setTimeout(() => {
      trackSdkPageView({
        url: window.location.href,
        path: window.location.pathname
      }).catch(error => {
        warn('[Rebrandly SDK] Failed to track SPA page_view:', error.message);
      });
    }, 0);
  };

  window.addEventListener('popstate', firePageView);

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function(...args) {
    originalPushState(...args);
    firePageView();
  };

  history.replaceState = function(...args) {
    originalReplaceState(...args);
    firePageView();
  };

  spaListeners = { firePageView, originalPushState, originalReplaceState };
}

/**
 * Remove SPA navigation listeners and restore original history methods.
 * Called on re-init to avoid stacking listeners.
 */
function teardownSpaNavigation() {
  if (!spaListeners || typeof window === 'undefined') return;

  window.removeEventListener('popstate', spaListeners.firePageView);

  if (spaListeners.originalPushState) {
    history.pushState = spaListeners.originalPushState;
  }
  if (spaListeners.originalReplaceState) {
    history.replaceState = spaListeners.originalReplaceState;
  }

  spaListeners = null;
}

/**
 * Get the current configuration (for debugging)
 * Note: API key is not returned for security
 * @returns {Object} Current configuration
 */
function getConfig() {
  return {
    initialized: config.initialized,
    apiBase: config.apiBase,
    hasApiKey: !!config.apiKey,
    deduplication: {
      enabled: config.deduplication.enabled,
      timeWindow: config.deduplication.timeWindow,
      retention: config.deduplication.retention
    }
  };
}

/**
 * Manually trigger queue processing
 * Useful for testing or forcing immediate retry of queued events
 * @returns {Promise<Object>} Result of queue processing
 */
async function processQueueNow() {
  if (!config.initialized) {
    logError('[Rebrandly SDK] SDK not initialized. Call rbly.init() first.');
    return {
      success: false,
      error: 'not_initialized',
      message: 'SDK not initialized'
    };
  }

  // Use skipQueue=true to prevent infinite loops
  const sendEventForQueue = (endpoint, payload, apiKey) => {
    return sendEvent(endpoint, payload, apiKey, true);
  };

  return await processQueue(sendEventForQueue);
}

// Export the public API
const rbly = {
  init,
  convert,
  track,
  getConfig,
  clearDedupCache,
  getDedupStats,
  getQueueStats,
  clearQueue,
  processQueueNow
};

// Make available globally and as module export
if (typeof window !== 'undefined') {
  window.rbly = rbly;
}

/**
 * Auto-initialization from script tag data attributes
 * Allows SDK to initialize without explicit init() call
 *
 * Usage:
 * <script src="rbly.js"
 *         data-api-key="your-api-key"
 *         data-api-base="https://tracking.api.rebrandly.com"
 *         data-auto-pageview="true"
 *         data-custom-utm-params="utm_custom,utm_other"></script>
 */
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Find the current script tag
  const currentScript = document.currentScript ||
    (function() {
      const scripts = document.getElementsByTagName('script');
      return scripts[scripts.length - 1];
    })();

  if (currentScript) {
    const apiKey = currentScript.getAttribute('data-api-key');

    // Auto-initialize if api-key is provided
    if (apiKey) {
      const autoInitConfig = {
        apiKey: apiKey
      };

      // Optional: API base URL
      const apiBase = currentScript.getAttribute('data-api-base');
      if (apiBase) {
        autoInitConfig.apiBase = apiBase;
      }

      // Optional: Auto pageview
      const autoPageView = currentScript.getAttribute('data-auto-pageview');
      if (autoPageView !== null) {
        autoInitConfig.autoPageView = autoPageView !== 'false';
      }

      // Optional: Custom UTM params
      const customUtmParams = currentScript.getAttribute('data-custom-utm-params');
      if (customUtmParams) {
        autoInitConfig.customUtmParams = customUtmParams.split(',').map(p => p.trim());
      }

      // Optional: Verify parameter name
      const verifyParam = currentScript.getAttribute('data-verify-param');
      if (verifyParam) {
        autoInitConfig.verifyParam = verifyParam;
      }

      // Optional: Deduplication settings
      const dedupEnabled = currentScript.getAttribute('data-dedup-enabled');
      if (dedupEnabled !== null) {
        autoInitConfig.deduplication = autoInitConfig.deduplication || {};
        autoInitConfig.deduplication.enabled = dedupEnabled !== 'false';
      }

      const dedupTimeWindow = currentScript.getAttribute('data-dedup-time-window');
      if (dedupTimeWindow) {
        autoInitConfig.deduplication = autoInitConfig.deduplication || {};
        autoInitConfig.deduplication.timeWindow = parseInt(dedupTimeWindow, 10);
      }

      // Initialize SDK automatically
      try {
        init(autoInitConfig);
        log('[Rebrandly SDK] Auto-initialized from script tag attributes');
      } catch (error) {
        logError('[Rebrandly SDK] Auto-initialization failed:', error.message);
      }
    }
  }
}

export default rbly;
